import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';
import { HotelFilter } from '@apps/main/new-desktop/modules/hotelFilters';

import type { StarsRatingFilter } from '@libs/trip-planner-interfaces';
import {
  AccommodationRule,
  AccommodationType,
  AmenitiesFilter,
  IngredientType,
  BoardBasisFilter,
  ExperienceFilter,
} from '@libs/trip-planner-interfaces';
import { exhaustiveGuard } from '@libs/utils';

import {
  setFilterText,
  setNeighborhoodPolygonsFilter,
  setStarsRating,
  toggleAmenitiesFilter,
  toggleBoardBasisFilter,
  toggleExperienceFilter,
  toggleFreeCancellation,
  toggleHotelType,
} from './hotelFilters';

export function handleFilterUpdate(
  type: HotelFilter,
  ingredient: HotelIngredient,
  filterUpdate?: string | StarsRatingFilter | boolean
): void {
  switch (type) {
    case HotelFilter.HotelName:
      setFilterText(filterUpdate as string, ingredient, IngredientType.Hotel);
      break;

    case HotelFilter.RuleFreeCancellation:
      toggleFreeCancellation(AccommodationRule.FreeCancellation, ingredient);
      break;

    case HotelFilter.AccommodationTypeHotel:
      toggleHotelType(AccommodationType.Hotel, ingredient);
      break;

    case HotelFilter.AccommodationTypeApartment:
      toggleHotelType(AccommodationType.Apartment, ingredient);
      break;

    case HotelFilter.AccommodationTypeHouseOrCottage:
      toggleHotelType(AccommodationType.HouseOrCottage, ingredient);
      break;

    case HotelFilter.RatingStars:
      setStarsRating(filterUpdate as StarsRatingFilter, ingredient);
      break;

    case HotelFilter.MealsNoMeals:
      toggleBoardBasisFilter(BoardBasisFilter.RoomOnly, ingredient);
      break;

    case HotelFilter.MealsBreakfast:
      toggleBoardBasisFilter(BoardBasisFilter.Breakfast, ingredient);
      break;

    case HotelFilter.MealsHalfBoard:
      toggleBoardBasisFilter(BoardBasisFilter.HalfBoard, ingredient);
      break;

    case HotelFilter.MealsFullBoard:
      toggleBoardBasisFilter(BoardBasisFilter.FullBoard, ingredient);
      break;

    case HotelFilter.MealsAllInclusive:
      toggleBoardBasisFilter(BoardBasisFilter.AllInclusive, ingredient);
      break;

    case HotelFilter.AmenitiesPool:
      toggleAmenitiesFilter(AmenitiesFilter.Pool, ingredient);
      break;

    case HotelFilter.AmenitiesSpa:
      toggleAmenitiesFilter(AmenitiesFilter.Spa, ingredient);
      break;

    case HotelFilter.AmenitiesGym:
      toggleAmenitiesFilter(AmenitiesFilter.Gym, ingredient);
      break;

    case HotelFilter.AmenitiesWifi:
      toggleAmenitiesFilter(AmenitiesFilter.Wifi, ingredient);
      break;

    case HotelFilter.AmenitiesAirConditioned:
      toggleAmenitiesFilter(AmenitiesFilter.AirConditioned, ingredient);
      break;

    case HotelFilter.AmenitiesVehicleChargingStation:
      toggleAmenitiesFilter(AmenitiesFilter.VehicleChargingStation, ingredient);
      break;

    case HotelFilter.AmenitiesParking:
      toggleAmenitiesFilter(AmenitiesFilter.Parking, ingredient);
      break;

    case HotelFilter.ExperienceBusiness:
      toggleExperienceFilter(ExperienceFilter.Business, ingredient);
      break;

    case HotelFilter.ExperienceFamily:
      toggleExperienceFilter(ExperienceFilter.Family, ingredient);
      break;

    case HotelFilter.ExperiencePetsAllowed:
      toggleExperienceFilter(ExperienceFilter.PetsAllowed, ingredient);
      break;

    case HotelFilter.ExperienceGreenSustainableProperty:
      toggleExperienceFilter(
        ExperienceFilter.GreenSustainableProperty,
        ingredient
      );
      break;

    case HotelFilter.NeighborhoodPolygons:
      setNeighborhoodPolygonsFilter(filterUpdate as string, ingredient);
      break;

    default:
      exhaustiveGuard(
        type,
        'Unhandled filterUpdate.type in hotelSelectionStore.ts'
      );
  }
}
